import { IJobGoalItem, IJobGoals, IJobMatchProfile, IJobmatchSearchParams } from '../../../common/types/JobMatchTypes'
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators"
import { apiUrl } from '@app/componentlib/src/application/helpers/api'
import { httpService } from '@app/componentlib/src/services/HttpService'
import store from '@app/componentlib/src/store'
import Vue from 'vue'
import { IJobPosting, IJobmatchResult, ISettingsItem, IUserInfo } from "@app/componentlib/common/types/JobSearchTypes"
import { convertPropertyNames, camelCaseToPascalCase } from '@app/componentlib/src/application/helpers/convertPropNames'
import { LocationModule } from '@app/componentlib/src/store/modules/locationdata'
import { JobMatchOccupationModule } from './jobMatchOccupations'
import moment from 'moment'
import { mapInformationList } from '@app/componentlib/src/application/helpers/constants'
import { JobSearchModule } from '../jobSearch'
import { validCountries } from '@app/componentlib/src/application/helpers/constants'

export interface IJobMatch {
  _initialized: boolean
  _jobResult: IJobmatchResult | undefined
  _jobPostings: IJobPosting[] | undefined
  //used for showing active job posting on JobDetailsView
  _selectedJobPosting: IJobPosting | undefined
  _selectedMobileJobPosting: IJobPosting | undefined
  _JobMatchProfile: IJobMatchProfile
  _showResultPage: boolean
  _userInfo: IUserInfo | undefined
  //Active index is used on JobResultList
  _activeIndex: number
  _resizeDoneDesktop: boolean
  _deepLinkJobId: number
  _advert: IJobPosting | undefined
  _jobGoals: IJobGoals[]
  _showAllOccupations: boolean
  _settings: ISettingsItem
}

const jobMatchApi = 'JobMatchApi'

@Module({
  dynamic: true,
  store,
  name: 'jobmatchdata',
  namespaced: true
})

class JobMatch extends VuexModule implements IJobMatch {
  _initialized: boolean = false
  _jobResult: IJobmatchResult | undefined
  _jobPostings: IJobPosting[] | undefined
  _selectedJobPosting: IJobPosting | undefined
  _selectedMobileJobPosting: IJobPosting | undefined
  _JobMatchProfile: IJobMatchProfile = {}
  _jobSearchParams: IJobmatchSearchParams = {
    order: 'DateDescending',
    limit: 50,
    offset: 0,
  }
  _jobmatchcount = 0
  _userInfo: IUserInfo | undefined
  _showResultPage: boolean = false
  _activeIndex = 0
  _resizeDoneDesktop: boolean = false
  _deepLinkJobId = 0
  _advert: IJobPosting | undefined
  _jobGoals: IJobGoals[] = []
  _showAllOccupations: boolean = false
  _settings: ISettingsItem = {}

  @Action({ rawError: true })
  async init (userInfo: IUserInfo) {
    await this.getJobMatchCount()
    await this.setUserInfo(userInfo)
    await this.getJobMatchProfile()

    this.SET_INITIALIZED(true)
  }

  @Action({ rawError: true })
  async getJobMatchProfile() {
    let lang = this.userInfo?.languageCode !== '' ? this.userInfo?.languageCode.slice(0,2) : 'en'
    const reqUrl = apiUrl(jobMatchApi, 'GetJobMatchProfile', {language: lang})
    await httpService.get(reqUrl).then((data: any) => {
      console.log('JOB MATCH PROFILE: ', data)
      if (data !== null && data !== undefined) {
        this.SET_JOBMATCH_PROFILE(convertPropertyNames(data))
        LocationModule.setSelectedCountryBasedOnString(data.GeoSettings.Country ?? 'dk')
        // Add selected occupations to the selected occupations list
        if (this.jobMatchProfile.selectedOccupations !== null && this.jobMatchProfile.selectedOccupations !== undefined) {
          JobMatchOccupationModule.addOccupationsToSelectedOccupations(this.jobMatchProfile.selectedOccupations)

          if (this.jobMatchProfile.selectedOccupations.length > 0)
            this.setShowResultPage(true)
          else
            this.setShowResultPage(false)
        }
        JobSearchModule.setSearchFiltersFromObject(data.ProfileFilters)
      }
    })
  }

  @Action({ rawError: true})
  async getJobMatchCount() {
    const reqUrl = apiUrl(jobMatchApi, 'GetJobMatchCount')
    await httpService.get(reqUrl).then((data: any) => {
      console.log('GetJobMatchCount: ', data)
      if(data !== null && data !== undefined) {
        this.context.commit('SET_JOBMATCHCOUNT', data.CountTotal)
      }
    })
  }

  @Action({ rawError: true })
  async setUserInfo(userInfo: IUserInfo) {
    this.SET_USERINFO(userInfo)
  }

  @Action({ rawError: true })
  setShowResultPage(bool: boolean) {
    this.context.commit('SET_SHOWRESULTPAGE', bool)
  }

  @Action({ rawError: true })
  setSelectedJobPosting(jobPosting: IJobPosting | undefined) {
    this.SET_SELECTEDJOBPOSTING(jobPosting)
  }

  @Action({ rawError: true })
  async startSearch(isNewSearch: boolean) {
    // Check if new search, if yes then reset jobresult and jobpostings
    if (isNewSearch === true) {
      this.SET_JOBRESULT(undefined)
      this.SET_JOBPOSTINGS(undefined)
      this.setJobmatchSearchParams({
        order: 'DateDescending',
        limit: 50,
        offset: 0
      } as IJobmatchSearchParams)
    }

    // set the search params
    const reqUrl = apiUrl(jobMatchApi, 'GetMatchesForProfile')

    await httpService.post(reqUrl, this.jobmatchSearchParams).then((data: any) => {
      if (data !== null && data !== undefined) {
        console.log("GOT SOME JOBS", data)
        this.SET_JOBRESULT(data)
        const tempList = this.jobPostings !== undefined ? this.jobPostings.concat(data.Results) : data.Results
        this.SET_JOBPOSTINGS(tempList)
        if (window.innerWidth > 767 && this.jobPostings !== undefined) {
          this.SET_SELECTEDJOBPOSTING(this.jobPostings[this.activeIndex])
        }
        this.setJobmatchSearchParams({
          order: 'DateDescending',
          limit: 50,
          offset: this.jobmatchSearchParams.offset + this.jobmatchSearchParams.limit
        } as IJobmatchSearchParams)
      }
    })
  }

  @Action({ rawError: true })
  async setJobPostings(jobPostings: IJobPosting[]) {
    this.SET_JOBPOSTINGS(jobPostings)
  }

  @Action({ rawError: true })
  async updateJobMatchProfile(newProfile: IJobMatchProfile) {
    await this.setJobmatchGeo(newProfile)
    await this.setJobFilters(newProfile)
    // Convert to PascalCase and send to endpoint
    newProfile.jobMatchPageLastVisited = moment.utc().format()
    const convertedJobProfile = convertPropertyNames(newProfile, camelCaseToPascalCase)
    const reqUrl = apiUrl(jobMatchApi, 'UpdateJobMatchProfile')

    await httpService.put(reqUrl, convertedJobProfile)
    this.context.commit('SET_JOBMATCH_PROFILE', newProfile)
  }

  @Action({ rawError: true })
  async setJobmatchGeo (newProfile: IJobMatchProfile) {
    if (
      this.userInfo !== undefined &&
      (this.jobMatchProfile !== undefined ||
      this.jobMatchProfile !== null) &&
      (this.jobMatchProfile.geoSettings?.zipCode === undefined ||
      this.jobMatchProfile.geoSettings?.zipCode === null)
    ) {
      const country = validCountries.includes(this.userInfo?.countryCode.toLowerCase()) ? this.userInfo?.countryCode.toUpperCase() : 'DK'

      const postals  = await LocationModule.getFirstCountryTypeahead(country)
      const mapInformation = mapInformationList.find(x => x.Country === country.toUpperCase())
      let tempLocation = postals.find((x) => x.postal === (this.userInfo?.zipCode))

      if (tempLocation === undefined)
        tempLocation = postals.find((x) => x.postal.replace(" ", "") === (mapInformation?.ZipCode.replace(" ", "")))

      newProfile.geoSettings = {
        zipCode: tempLocation?.postal ?? '',
        city: tempLocation?.city ?? '',
        radiusKm: 50,
        longitude: tempLocation?.lon ?? undefined,
        latitude: tempLocation?.lat ?? undefined,
        country: country ?? ''
      }
    }
  }

  @Action({ rawError: true })
  setJobFilters(newProfile: IJobMatchProfile) {
    const { CareerFilter, IndustryFilter, StartDateString } = JobSearchModule.searchParams
    var id = 3

    if(StartDateString !== undefined) {
      if(StartDateString == 'LastMonth' || StartDateString == '4') {
        id = 4
      } else if(StartDateString == 'LastTwoWeeks' || StartDateString == '3') {
        id = 3
      } else if(StartDateString == 'LastWeek' || StartDateString == '2') {
        id = 2
      } else if(StartDateString == 'Last24Hours' || StartDateString == '1') {
        id = 1
      }
    }

    newProfile.profileFilters = {
      careerFilter: CareerFilter,
      industryFilter: IndustryFilter,
      dateFilter: id
    }
  }

  @Action({ rawError: true })
  emptyProfileLocationData() {
    let tempProfile = this.jobMatchProfile as IJobMatchProfile
    tempProfile.geoSettings = {
      latitude: 0,
      longitude: 0,
      zipCode: "",
      city: "",
      radiusKm: 0,
      country: LocationModule.selectedCountry.countryCode
    }
    this.updateJobMatchProfile(tempProfile)
  }

  @Action({ rawError: true })
  setJobmatchSearchParams(params: IJobmatchSearchParams) {
    this.SET_JOBMATCHSEARCHPARAMS(params)
  }

  @Action({ rawError: true })
  async setActiveIndex(index: number) {
    this.SET_ACTIVEINDEX(index)
  }

  @Action({ rawError: true })
  async setResizeDoneDesktop(resizeDone: boolean) {
    this.SET_RESIZEDONEDESKTOP(resizeDone)
  }

  @Action({ rawError: true })
  async setDeepLinkJobId(jobId: number) {
    this.SET_DEEPLINKJOBID(jobId)
  }

  @Action({ rawError: true })
  async setAdvert(jobPosting: IJobPosting | undefined) {
    this.SET_ADVERT(jobPosting)
  }


  @Action({ rawError: true })
  async getAdvert(jobId: number) {
    const reqUrl = apiUrl(jobMatchApi, 'GetAdvert?jobId='+jobId)

    await httpService.get(reqUrl).then((data: IJobPosting) => {
      if(data !== null && parseInt(data.Id) !== 0) {
        this.SET_ADVERT(data)
      }
    })
  }

  @Action({ rawError: true })
  async getJobGoals() {
    const api = 'JobGoalApi'
    const endpoint = 'GetJobGoals?userid=' + this.userInfo?.id
    await httpService.get(apiUrl(api, endpoint)).then((data: any) => {
      for(let item in data.categories as IJobGoals[]) {
        data.categories[item].goals.find((x: IJobGoalItem) => {
          if (JobMatchOccupationModule.selectedOccupations.find(o => o.label === x.text && o.occupationSource === 'JobGoal'))
            x.isActive = true
          else
            x.isActive = false
        })
      }
      this.SET_JOBGOALS(data.categories)
    })
  }

  @Action({rawError: true})
  async setShowAllOccupations(toggle: boolean) {
    this.SET_SHOWALLOCCUPATIONS(toggle)
  }

  @Action({ rawError: true })
  navigateToJobApplication(r: {url: string, jobId: string}) {
    // Here we want to redirect the user to job application with the jobposting jobid
    // Then we can make an job application from this specific jobpost.
    if(r.jobId !== undefined) {
      console.log(`Navigating to job application with ID: ${r.jobId}`)
      // Go to modern web app -> settings.modern_web_app_url
      const modernWebAppUrl = this.settings.ModernWebAppUrl

      function removeTrailingSlash(url: string): string {
        if (url.endsWith('/')) {
            return url.slice(0, -1)
        }
        return url
      }

      if(modernWebAppUrl !== undefined) {
        const newUrl = removeTrailingSlash(modernWebAppUrl) + r.url + '?jobid=' + r.jobId
        console.log(newUrl, modernWebAppUrl)
        window.location.href = newUrl
      }
    }
  }

  @Action({ rawError: true })
  setSettings(obj?: object) {
    // Here we want to redirect the user to job application with the jobposting jobid
    // Then we can make an job application from this specific jobpost.
    if(obj !== undefined) {
      this.SET_SETTINGS(obj)
    }
  }


  // Mutations
  @Mutation
  SET_INITIALIZED (val: boolean) {
    Vue.set(this, '_initialized', val)
  }
  @Mutation
  SET_JOBMATCH_PROFILE(profile: IJobMatchProfile) {
    Vue.set(this, '_JobMatchProfile', profile)
  }
  @Mutation
  SET_USERINFO(userInfo: IUserInfo) {
    Vue.set(this, '_userInfo', userInfo)
  }

  @Mutation
  SET_JOBMATCHCOUNT(count: number) {
    Vue.set(this, '_jobmatchcount', count)
  }

  @Mutation
  SET_JOBRESULT(jobResult?: IJobmatchResult) {
    Vue.set(this, "_jobResult", jobResult)
  }

  @Mutation
  SET_JOBPOSTINGS(jobPostings?: IJobPosting[]) {
    Vue.set(this, "_jobPostings", jobPostings)
  }

  @Mutation
  SET_JOBMATCHSEARCHPARAMS(params: IJobmatchSearchParams) {
    Vue.set(this, '_jobSearchParams', params)
  }

  @Mutation
  SET_SELECTEDJOBPOSTING(jobPosting: IJobPosting | undefined) {
    Vue.set(this, "_selectedJobPosting", jobPosting)
  }

  @Mutation
  SET_SHOWRESULTPAGE(bool: boolean) {
    Vue.set(this, '_showResultPage', bool)
  }

  @Mutation
  SET_ACTIVEINDEX (index: number) {
    Vue.set(this, '_activeIndex', index)
  }

  @Mutation
  SET_RESIZEDONEDESKTOP (resizeDone: boolean) {
    Vue.set(this, '_resizeDoneDesktop', resizeDone)
  }

  @Mutation
  SET_DEEPLINKJOBID (jobId: number) {
    Vue.set(this, '_deepLinkJobId', jobId)
  }

  @Mutation
  SET_ADVERT (advert?: IJobPosting) {
    Vue.set(this, '_advert', advert)
  }

  @Mutation
  SET_JOBGOALS (jobGoals: IJobGoals[]) {
    Vue.set(this, '_jobGoals', jobGoals)
  }

  @Mutation
  SET_SHOWALLOCCUPATIONS (toggle: Boolean) {
    Vue.set(this, '_showAllOccupations', toggle)
  }
  @Mutation
  SET_SETTINGS (obj: object) {
    Vue.set(this, '_settings', obj)
  }

  // Getters
  get initialized (): boolean {
    return this._initialized
  }

  get jobResult (): IJobmatchResult | undefined {
    return this._jobResult
  }

  get jobPostings (): IJobPosting[] | undefined {
    return this._jobPostings
  }

  get selectedJobPosting (): IJobPosting | undefined {
    return this._selectedJobPosting
  }

  get selectedMobileJobPosting (): IJobPosting | undefined {
    return this._selectedMobileJobPosting
  }
  get jobMatchProfile (): IJobMatchProfile {
    return this._JobMatchProfile
  }

  get userInfo (): IUserInfo | undefined {
    return this._userInfo
  }

  get jobMatchCount () : number {
    return this._jobmatchcount
  }

  get jobmatchSearchParams (): IJobmatchSearchParams {
    return this._jobSearchParams
  }

  get showResultPage () : boolean {
    return this._showResultPage
  }

  get activeIndex(): number {
    return this._activeIndex
  }

  get resizeDoneDesktop(): boolean {
    return this._resizeDoneDesktop
  }

  get deepLinkJobId(): number {
    return this._deepLinkJobId
  }

  get advert(): IJobPosting | undefined {
    return this._advert
  }

  get jobGoals(): IJobGoals[] {
    return this._jobGoals
  }

  get showAllOccupations(): boolean {
    return this._showAllOccupations
  }

  get settings (): ISettingsItem {
    return this._settings
  }
}

export const JobMatchModule = getModule(JobMatch)